/**
 * Central i18n configuration. Adding a new language is a content task:
 * add it to AVAILABLE_LANGS, map it to a BCP-47 locale in LANG_TO_LOCALE,
 * give it a display label, and drop the translation JSON under src/assets/i18n.
 */

/** Source/default language. English is the source of truth for all keys. */
export const DEFAULT_LANG = 'en';

/**
 * Pseudo-locale used to surface hardcoded strings and text-expansion/truncation
 * issues before real translations exist. Generated from `en` (see tools/i18n).
 */
export const PSEUDO_LANG = 'en-PSEUDO';

/** Languages the app can switch to at runtime. English only; the profile switcher is removed. */
export const AVAILABLE_LANGS: readonly string[] = [DEFAULT_LANG];

/** Maps an app language to the BCP-47 locale used for date/number/currency formatting. */
export const LANG_TO_LOCALE: Readonly<Record<string, string>> = {
  [DEFAULT_LANG]: 'en-US',
  [PSEUDO_LANG]: 'en-US',
};

/** Human-readable labels for the locale switcher. */
export const LANG_LABELS: Readonly<Record<string, string>> = {
  [DEFAULT_LANG]: 'English',
  [PSEUDO_LANG]: 'Pseudo (QA)',
};

/** localStorage key holding the last chosen language for fast bootstrap. */
export const LOCALE_STORAGE_KEY = 'IDEAL_LOCALE';

/** Resolves an arbitrary language/locale string to a supported app language. */
export function resolveSupportedLang(candidate: string | null | undefined): string | null {
  if (!candidate) {
    return null;
  }
  const normalized = candidate.trim();
  if (AVAILABLE_LANGS.includes(normalized)) {
    return normalized;
  }
  // Fall back to a prefix match (e.g. "en-GB" -> "en").
  const prefix = normalized.split('-')[0].toLowerCase();
  return AVAILABLE_LANGS.find((lang) => lang.toLowerCase().split('-')[0] === prefix) ?? null;
}
